import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import debounce from "lodash.debounce";
import { FixedSizeList } from "react-window";
import { useChatContext } from "stream-chat-react";
import api from "../../utils/api";
import { apiUrls } from "../../utils/urls";

const PAGE_SIZE = 75;
const LIST_HEIGHT = 360;
const ROW_HEIGHT = 60;

export default function CreateChannelModal({ onClose }) {
  const { client } = useChatContext();
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedUsersById, setSelectedUsersById] = useState({});
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [channelName, setChannelName] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchingUsers, setFetchingUsers] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce((value) => {
        setSearchTerm(value.trim());
      }, 300),
    []
  );

  const fetchUsers = useCallback(
    async ({ pageToFetch, reset = false }) => {
      try {
        if (reset) {
          setFetchingUsers(true);
        } else {
          setLoadingMore(true);
        }
        const response = await api.get(apiUrls.messaging.users(), {
          params: {
            search_input: searchTerm,
            page: pageToFetch,
            size: PAGE_SIZE,
          },
        });
        const data = response.data;
        const incomingUsers = data.users || [];
        const incomingTotal = data.total_count || 0;

        setTotalCount(incomingTotal);
        setHasMore((pageToFetch + 1) * PAGE_SIZE < incomingTotal);
        setUsers((prev) => {
          if (reset) {
            return incomingUsers;
          }
          const existingIds = new Set(prev.map((user) => user.id));
          const merged = [...prev];
          for (const user of incomingUsers) {
            if (!existingIds.has(user.id)) {
              merged.push(user);
            }
          }
          return merged;
        });
      } catch (err) {
        setError(err?.response?.data?.error || err.message || "Failed to load users.");
      } finally {
        if (reset) {
          setFetchingUsers(false);
        } else {
          setLoadingMore(false);
        }
      }
    },
    [searchTerm]
  );

  useEffect(() => {
    debouncedSetSearchTerm(searchInput);
    return () => {
      debouncedSetSearchTerm.cancel();
    };
  }, [searchInput, debouncedSetSearchTerm]);

  useEffect(() => {
    setPage(0);
    setHasMore(true);
    fetchUsers({ pageToFetch: 0, reset: true });
  }, [searchTerm, fetchUsers]);

  const toggleUser = useCallback((user) => {
    setSelectedIds((prev) =>
      prev.includes(user.id)
        ? prev.filter((value) => value !== user.id)
        : [...prev, user.id]
    );
    setSelectedUsersById((prev) => {
      const next = { ...prev };
      if (next[user.id]) {
        delete next[user.id];
      } else {
        next[user.id] = user;
      }
      return next;
    });
  }, []);

  const loadMoreIfNeeded = useCallback(
    ({ visibleStopIndex }) => {
      if (fetchingUsers || !hasMore) {
        return;
      }
      if (visibleStopIndex < users.length - 8) {
        return;
      }
      const nextPage = page + 1;
      setPage(nextPage);
      fetchUsers({ pageToFetch: nextPage, reset: false });
    },
    [fetchingUsers, hasMore, users.length, page, fetchUsers]
  );

  async function handleSubmit() {
    if (!selectedIds.length) {
      setError("Select at least one member.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await api.post(apiUrls.messaging.channels(), {
          name: channelName,
          member_ids: selectedIds,
      });
      const data = response.data;
      const channel = client.channel(data.channel_type, data.channel_id);
      await channel.watch();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Failed to create channel.");
    } finally {
      setLoading(false);
    }
  }

  const selectedUsers = useMemo(
    () => selectedIds.map((id) => selectedUsersById[id]).filter(Boolean),
    [selectedIds, selectedUsersById]
  );

  const rowData = useMemo(
    () => ({
      users,
      selectedIds,
      onToggle: toggleUser,
    }),
    [users, selectedIds, toggleUser]
  );

  const Row = ({ index, style, data }) => {
    const user = data.users[index];
    const isSelected = data.selectedIds.includes(user.id);
    return (
      <div style={style}>
        <ListItemButton onClick={() => data.onToggle(user)} dense divider>
          <Checkbox checked={isSelected} tabIndex={-1} />
          <ListItemText
            primary={user.name}
            secondary={`${user.email} · ${user.role}`}
            secondaryTypographyProps={{ noWrap: true }}
          />
        </ListItemButton>
      </div>
    );
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>New Chat</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ pt: 1 }}>

        <TextField
          label="Chat name (optional)"
          size="small"
          fullWidth
          value={channelName}
          onChange={(event) => setChannelName(event.target.value)}
          sx={{ mb: 2 }}
        />

        <TextField
          label="Search users by name or email"
          size="small"
          fullWidth
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          sx={{ mb: 1 }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Showing {users.length} of {totalCount} users
        </Typography>

        {fetchingUsers ? (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: LIST_HEIGHT }}>
            <CircularProgress size={24} />
          </Box>
        ) : users.length === 0 ? (
          <Box sx={{ minHeight: LIST_HEIGHT, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography color="text.secondary">No users found for this search.</Typography>
          </Box>
        ) : (
          <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, mb: 2 }}>
            <FixedSizeList
              height={LIST_HEIGHT}
              width="100%"
              itemCount={users.length}
              itemSize={ROW_HEIGHT}
              itemData={rowData}
              onItemsRendered={loadMoreIfNeeded}
            >
              {Row}
            </FixedSizeList>
            {loadingMore && (
              <Box sx={{ py: 1, display: "flex", justifyContent: "center" }}>
                <CircularProgress size={18} />
              </Box>
            )}
          </Box>
        )}

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Selected ({selectedIds.length})
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, minHeight: 36 }}>
          {selectedUsers.map((user) => (
            <Chip
              key={user.id}
              label={user.name}
              onDelete={() => toggleUser(user)}
              size="small"
            />
          ))}
        </Box>

        {error && <Typography sx={{ color: "error.main", mt: 1 }}>{error}</Typography>}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !selectedIds.length}
        >
          {loading ? "Creating..." : "Create Chat"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
